import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Tooltip, Avatar, Badge, Dropdown, Input, Space, Typography, Drawer, Button } from 'antd';
import { 
  BellOutlined, 
  SearchOutlined, 
  UserOutlined, 
  MenuOutlined, 
  LogoutOutlined, 
  SettingOutlined,
  AppstoreOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';

const { Content, Sider, Header } = Layout;
const { Text, Title } = Typography;

const AdminPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredPath, setHoveredPath] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState(['content-management']);

  const toggleSection = (sectionName) => {
    setOpenSections(prev => 
      prev.includes(sectionName) 
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    );
  };

  const siderWidth = 260;
  const headerHeight = 80;
  const primaryColor = "#3C007A";
  const activeBg = "#f0ebf8";
  const inactiveColor = "#333";
  const hoverBg = "#f9fafb";
  const accentColor = "#FF8F00";

  // Admin profile data
  const adminProfile = {
    name: "Admin User",
    role: "System Administrator",
    avatarUrl: null,
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  // Admin dropdown menu items
  const adminMenuItems = [
    {
      key: '1',
      label: (
        <Space direction="vertical" size={0} style={{ padding: '4px 0' }}>
          <Text strong>{adminProfile.name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{adminProfile.role}</Text>
        </Space>
      ),
    },
    { type: 'divider' },
    // {
    //   key: '2',
    //   icon: <UserOutlined />,
    //   label: <Link to="/admin/profile">My Profile</Link>,
    // },
    // {
    //   key: '3',
    //   icon: <SettingOutlined />,
    //   label: <Link to="/admin/settings">Settings</Link>,
    // },
    { type: 'divider' },
    {
      key: '4',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      style: { backgroundColor: '#FF8F00', color: '#fff' },
    },
  ];

  const menuItems = [
    { 
      name: 'Dashboard', 
      path: '/admin', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M7.5 2.5H3.33333C2.8731 2.5 2.5 2.8731 2.5 3.33333V9.16667C2.5 9.6269 2.8731 10 3.33333 10H7.5C7.96024 10 8.33333 9.6269 8.33333 9.16667V3.33333C8.33333 2.8731 7.96024 2.5 7.5 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16.668 2.5H12.5013C12.0411 2.5 11.668 2.8731 11.668 3.33333V5.83333C11.668 6.29357 12.0411 6.66667 12.5013 6.66667H16.668C17.1282 6.66667 17.5013 6.29357 17.5013 5.83333V3.33333C17.5013 2.8731 17.1282 2.5 16.668 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16.668 10H12.5013C12.0411 10 11.668 10.3731 11.668 10.8333V16.6667C11.668 17.1269 12.0411 17.5 12.5013 17.5H16.668C17.1282 17.5 17.5013 17.1269 17.5013 16.6667V10.8333C17.5013 10.3731 17.1282 10 16.668 10Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7.5 13.3334H3.33333C2.8731 13.3334 2.5 13.7065 2.5 14.1667V16.6667C2.5 17.1269 2.8731 17.5 3.33333 17.5H7.5C7.96024 17.5 8.33333 17.1269 8.33333 16.6667V14.1667C8.33333 13.7065 7.96024 13.3334 7.5 13.3334Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    { 
      name: 'Companies', 
      path: '/admin/companies', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M3.33333 17.5V5.83333C3.33333 5.39131 3.50893 4.96738 3.82149 4.65482C4.13405 4.34226 4.55797 4.16667 5 4.16667H10C10.442 4.16667 10.866 4.34226 11.1785 4.65482C11.4911 4.96738 11.6667 5.39131 11.6667 5.83333V17.5M1.66667 17.5H18.3333M5.83333 7.5H7.5M5.83333 10.8333H7.5M5.83333 14.1667H7.5M13.3333 7.5H15M13.3333 10.8333H15M13.3333 14.1667H15M11.6667 17.5V9.16667C11.6667 8.72464 11.8423 8.30072 12.1548 7.98816C12.4674 7.67559 12.8913 7.5 13.3333 7.5H15C15.442 7.5 15.866 7.67559 16.1785 7.98816C16.4911 8.30072 16.6667 8.72464 16.6667 9.16667V17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Associations', 
      path: '/admin/associations', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M14.1667 17.5V15.8333C14.1667 14.9493 13.8155 14.1014 13.1904 13.4763C12.5652 12.8512 11.7174 12.5 10.8333 12.5H4.16667C3.28262 12.5 2.43477 12.8512 1.80965 13.4763C1.18453 14.1014 0.833334 14.9493 0.833334 15.8333V17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7.50033 9.16667C9.34127 9.16667 10.8337 7.67428 10.8337 5.83333C10.8337 3.99238 9.34127 2.5 7.50033 2.5C5.65938 2.5 4.16699 3.99238 4.16699 5.83333C4.16699 7.67428 5.65938 9.16667 7.50033 9.16667Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19.1654 17.5V15.8333C19.1648 15.0948 18.9202 14.3773 18.4679 13.7936C18.0156 13.2099 17.3816 12.793 16.6654 12.6083" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M13.332 2.60834C14.0505 2.79192 14.6867 3.20892 15.1403 3.7936C15.5939 4.37827 15.8389 5.09736 15.8389 5.8375C15.8389 6.57765 15.5939 7.29674 15.1403 7.88141C14.6867 8.46609 14.0505 8.88309 13.332 9.06667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Academia', 
      path: '/admin/academia', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M10 4.16667L2.5 7.5L10 10.8333L17.5 7.5L10 4.16667Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2.5 12.5L10 15.8333L17.5 12.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2.5 7.5L10 10.8333L17.5 7.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Startups', 
      path: '/admin/startups', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
          <path d="M12 15L12 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 6L12 2L8 6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21 16C21 19.866 16.97 23 12 23C7.03 23 3 19.866 3 16C3 13.904 4.13 11.979 6 10.826" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 12L12 15L16 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Incubators', 
      path: '/admin/incubators', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
          <path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5 21V7L12 3L19 7V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 9H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 13H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 17H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 9H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 13H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 17H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Investors', 
      path: '/admin/investors', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
          <path d="M12 2V6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 18V22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4.93 4.93L7.76 7.76" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16.24 16.24L19.07 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12H6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M18 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4.93 19.07L7.76 16.24" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16.24 7.76L19.07 4.93" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5"/>
        </svg>
      )
    },
    { 
      name: 'Consultants', 
      path: '/admin/consultants', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="6" r="4" stroke={color} strokeWidth="1.5"/>
          <path d="M19 20C19 15.58 15.42 12 12 12C8.58 12 5 15.58 5 20" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M16 20L17.5 18.5L19 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Districts', 
      path: '/admin/districts', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M10 10.8333C11.841 10.8333 13.3333 9.34095 13.3333 7.5C13.3333 5.65905 11.841 4.16667 10 4.16667C8.15905 4.16667 6.66667 5.65905 6.66667 7.5C6.66667 9.34095 8.15905 10.8333 10 10.8333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 18.3333C13.3333 15 16.6667 12.0152 16.6667 7.5C16.6667 5.67392 15.9417 3.92212 14.6539 2.63439C13.3661 1.34667 11.6143 0.621704 9.78833 0.621704C7.96225 0.621704 6.21045 1.34667 4.92272 2.63439C3.635 3.92212 2.91003 5.67392 2.91003 7.5C2.91003 12.0152 6.66667 15 10 18.3333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Industries', 
      path: '/admin/industries', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M2.5 17.5V7.5L7.5 3.33333L12.5 7.5M12.5 5.83333V2.5H15V7.08333M12.5 17.5V11.6667C12.5 11.2246 12.6756 10.8007 12.9882 10.4882C13.3007 10.1756 13.7246 10 14.1667 10H17.5V17.5M1.66667 17.5H18.3333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Investor Requests', 
      path: '/admin/investor-requests', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 5.83333V10L12.5 12.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      type: 'section',
      name: 'Content Management',
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M1.66667 10H18.3333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 1.66667C12.0844 3.94863 13.269 6.91003 13.3333 10C13.269 13.09 12.0844 16.0514 10 18.3333C7.91557 16.0514 6.73104 13.09 6.66667 10C6.73104 6.91003 7.91557 3.94863 10 1.66667Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      items: [
        { 
          name: 'Executive Committee', 
          path: '/admin/members', 
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M14.1667 17.5V15.8333C14.1667 14.9493 13.8155 14.1014 13.1904 13.4763C12.5652 12.8512 11.7174 12.5 10.8333 12.5H4.16667C3.28262 12.5 2.43477 12.8512 1.80965 13.4763C1.18453 14.1014 0.833334 14.9493 0.833334 15.8333V17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.50033 9.16667C9.34127 9.16667 10.8337 7.67428 10.8337 5.83333C10.8337 3.99238 9.34127 2.5 7.50033 2.5C5.65938 2.5 4.16699 3.99238 4.16699 5.83333C4.16699 7.67428 5.65938 9.16667 7.50033 9.16667Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19.1654 17.5V15.8333C19.1648 15.0948 18.9202 14.3773 18.4679 13.7936C18.0156 13.2099 17.3816 12.793 16.6654 12.6083" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.332 2.60834C14.0505 2.79192 14.6867 3.20892 15.1403 3.7936C15.5939 4.37827 15.8389 5.09736 15.8389 5.8375C15.8389 6.57765 15.5939 7.29674 15.1403 7.88141C14.6867 8.46609 14.0505 8.88309 13.332 9.06667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
        { 
          name: 'Sliders', 
          path: '/admin/sliders', 
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.08333 8.33333C7.77369 8.33333 8.33333 7.77369 8.33333 7.08333C8.33333 6.39298 7.77369 5.83333 7.08333 5.83333C6.39298 5.83333 5.83333 6.39298 5.83333 7.08333C5.83333 7.77369 6.39298 8.33333 7.08333 8.33333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 12.5L13.3333 8.33333L4.16667 17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
        { 
          name: 'Top Header', 
          path: '/admin/top-utility', 
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M10 1.66667V6.66667M10 1.66667L7.5 4.16667M10 1.66667L12.5 4.16667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.8333 5H17.5C17.721 5 17.933 5.0878 18.0893 5.24408C18.2455 5.40036 18.3333 5.61232 18.3333 5.83333V16.6667C18.3333 16.8877 18.2455 17.0996 18.0893 17.2559C17.933 17.4122 17.721 17.5 17.5 17.5H2.5C2.27899 17.5 2.06703 17.4122 1.91075 17.2559C1.75447 17.0996 1.66667 16.8877 1.66667 16.6667V5.83333C1.66667 5.61232 1.75447 5.40036 1.91075 5.24408C2.06703 5.0878 2.27899 5 2.5 5H4.16667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.83333 10H14.1667M5.83333 13.3333H14.1667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
        { 
          name: 'Media Gallery', 
          path: '/admin/gallery', 
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.08333 8.33333C7.77369 8.33333 8.33333 7.77369 8.33333 7.08333C8.33333 6.39298 7.77369 5.83333 7.08333 5.83333C6.39298 5.83333 5.83333 6.39298 5.83333 7.08333C5.83333 7.77369 6.39298 8.33333 7.08333 8.33333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 12.5L13.3333 8.33333L4.16667 17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
        {
          name: 'Press Releases',
          path: '/admin/press-releases',
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M3.33333 2.5H12.5L16.6667 6.66667V16.6667C16.6667 17.1087 16.4911 17.5326 16.1785 17.8452C15.866 18.1577 15.442 18.3333 15 18.3333H3.33333C2.89131 18.3333 2.46738 18.1577 2.15482 17.8452C1.84226 17.5326 1.66667 17.1087 1.66667 16.6667V4.16667C1.66667 3.72464 1.84226 3.30072 2.15482 2.98816C2.46738 2.67559 2.89131 2.5 3.33333 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.5 2.5V6.66667H16.6667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 10.8333H11.6667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 14.1667H11.6667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 7.5H6.66667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
        {
          name: 'Advantage Vidarbha',
          path: '/admin/blog-highlights',
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M17.5 6.66667L10 2.5L2.5 6.66667L10 10.8333L17.5 6.66667Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.5 10L10 14.1667L17.5 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.5 13.3333L10 17.5L17.5 13.3333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
        {
          name: 'Blog Posts',
          path: '/admin/blog-posts',
          icon: (color) => (
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M3.33333 2.5H12.5L16.6667 6.66667V16.6667C16.6667 17.1087 16.4911 17.5326 16.1785 17.8452C15.866 18.1577 15.442 18.3333 15 18.3333H3.33333C2.89131 18.3333 2.46738 18.1577 2.15482 17.8452C1.84226 17.5326 1.66667 17.1087 1.66667 16.6667V4.16667C1.66667 3.72464 1.84226 3.30072 2.15482 2.98816C2.46738 2.67559 2.89131 2.5 3.33333 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.5 2.5V6.66667H16.6667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.66667 11.25H13.3333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.66667 14.5833H10.8333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        },
      ]
    },
    { 
      name: 'Policy Management', 
      path: '/admin/policies', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M10 18.3333C10 18.3333 16.6667 15 16.6667 10V4.16667L10 1.66667L3.33333 4.16667V10C3.33333 15 10 18.3333 10 18.3333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      name: 'Contact Submissions', 
      path: '/admin/contact-submissions', 
      icon: (color) => (
        <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
          <path d="M3.33333 3.33333H16.6667C17.5833 3.33333 18.3333 4.08333 18.3333 5V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33333C2.41667 16.6667 1.66667 15.9167 1.66667 15V5C1.66667 4.08333 2.41667 3.33333 3.33333 3.33333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M18.3333 5L10 10.8333L1.66667 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
  ];

  const getLinkStyle = (path) => {
    const isActive = location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));
    const isHovered = hoveredPath === path;

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: '14px',
      padding: collapsed ? '12px' : '12px 16px',
      margin: collapsed ? '0 8px' : '0 12px',
      borderRadius: '12px',
      color: isActive ? primaryColor : inactiveColor,
      backgroundColor: isActive ? activeBg : (isHovered ? hoverBg : 'transparent'),
      textDecoration: 'none',
      fontWeight: isActive ? 500 : 400,
      fontSize: '16px',
      fontFamily: "'Rubik', sans-serif",
      transition: 'all 0.2s ease-in-out',
      cursor: 'pointer'
    };
  };

  const getIconColor = (path) => {
    const isActive = location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));
    return isActive ? primaryColor : "#666666";
  };

  const MenuItem = ({ to, icon, label, path, isSubmenu = false }) => {
    const linkContent = (
      <Link
        to={to}
        style={{
          ...getLinkStyle(path),
          ...(isSubmenu && { paddingLeft: '48px' })
        }}
        onMouseEnter={() => setHoveredPath(path)}
        onMouseLeave={() => setHoveredPath(null)}
      >
        {icon(getIconColor(path))}
        {!collapsed && <span>{label}</span>}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip title={label} placement="right">
          {linkContent}
        </Tooltip>
      );
    }

    return linkContent;
  };

  return (
    <Layout style={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Admin Header Component */}
      <Header style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: "100%",
        background: "#ffffff",
        padding: "0 24px",
        boxShadow: "0 2px 8px #f0f1f2",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "80px",
        fontFamily: "'Rubik', sans-serif",
      }}>
        {/* LEFT: Logo & Welcome Message */}
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              alt="AID Logo"
              src="/img/aid.svg"
              style={{ height: "75px", width: "auto" }}
            />
          </Link>

          {/* Welcome Message */}
          <div style={{ 
            borderLeft: "1px solid #f0f0f0", 
            paddingLeft: "24px",
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Title level={3} style={{ 
              margin: 0, 
              fontFamily: 'Rubik, sans-serif',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: '1.2',
              marginBottom: '0'
            }}>
              <span style={{ color: primaryColor }}>Admin Panel</span>
            </Title>
            <Text type="secondary" style={{ 
              fontSize: '12px', 
              fontFamily: 'Rubik, sans-serif', 
              lineHeight: '1.4',
              marginTop: '3px'
            }}>
              System Management Dashboard
            </Text>
          </div>
        </div>

        {/* RIGHT: Actions & Profile */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Space size="large" className="d-none d-md-flex">
            {/* Search Bar */}
            <Input 
              placeholder="Search..." 
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} 
              bordered={false}
              style={{ 
                background: '#f5f5f5', 
                borderRadius: '8px', 
                width: '200px' 
              }} 
            />

            {/* Profile Dropdown */}
            <Dropdown menu={{ items: adminMenuItems }} placement="bottomRight" arrow>
              <Space style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '8px', transition: '0.3s' }}>
                <Avatar 
                  size="large" 
                  style={{ backgroundColor: primaryColor, verticalAlign: 'middle' }}
                  icon={<UserOutlined />}
                  src={adminProfile.avatarUrl}
                >
                  {adminProfile.name.charAt(0)}
                </Avatar>
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <Text strong style={{ fontSize: '16px', fontFamily: 'Rubik, sans-serif', fontWeight: 400 }}>{adminProfile.name}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', fontFamily: 'Rubik, sans-serif' }}>{adminProfile.role}</Text>
                </div>
              </Space>
            </Dropdown>
          </Space>

          {/* Mobile Menu Trigger */}
          <Button 
            className="d-md-none" 
            type="text" 
            icon={<MenuOutlined style={{ fontSize: '20px' }} />} 
            onClick={() => setMobileMenuOpen(true)}
          />
        </div>
      </Header>

      {/* Mobile Drawer */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ padding: '20px', background: '#f9f9f9', textAlign: 'center' }}>
           <Avatar size={64} style={{ backgroundColor: primaryColor, marginBottom: '10px' }}>
              {adminProfile.name.charAt(0)}
           </Avatar>
           <Title level={5} style={{ margin: 0, fontFamily: 'Rubik, sans-serif' }}>{adminProfile.name}</Title>
           <Text type="secondary" style={{ fontFamily: 'Rubik, sans-serif' }}>{adminProfile.role}</Text>
        </div>
        
        <div style={{ padding: '20px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
              <Space><AppstoreOutlined /> Dashboard</Space>
            </Link>
            <Link to="/admin/profile" onClick={() => setMobileMenuOpen(false)}>
               <Space><UserOutlined /> Profile</Space>
            </Link>
            <Link to="/admin/settings" onClick={() => setMobileMenuOpen(false)}>
               <Space><SettingOutlined /> Settings</Space>
            </Link>
            <Button 
              block 
              icon={<LogoutOutlined />} 
              onClick={handleLogout}
              style={{ backgroundColor: '#FF8F00', borderColor: '#FF8F00', color: '#fff' }}
            >
              Logout
            </Button>
          </Space>
        </div>
      </Drawer>

      <Layout hasSider style={{ marginTop: headerHeight }}>
        {/* Fixed Sidebar */}
        <Sider
          width={siderWidth}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth="80"
          style={{
            overflowY: 'auto',
            height: `calc(100vh - 80px)`,
            position: 'fixed',
            left: 0,
            top: 80,
            background: '#fff',
            zIndex: 900,
            borderRight: '1px solid #f0f0f0',
            scrollbarWidth: 'thin',
            scrollbarColor: '#d9d9d9 #f0f0f0'
          }}
          theme="light"
        >
          <style>
            {`
              .ant-layout-sider::-webkit-scrollbar {
                width: 8px;
              }
              .ant-layout-sider::-webkit-scrollbar-track {
                background: #f0f0f0;
              }
              .ant-layout-sider::-webkit-scrollbar-thumb {
                background: #d9d9d9;
                border-radius: 4px;
              }
              .ant-layout-sider::-webkit-scrollbar-thumb:hover {
                background: #bfbfbf;
              }
            `}
          </style>
          <div style={{ 
            paddingTop: '24px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '4px',
            paddingBottom: '24px'
          }}>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0, 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '4px' 
            }}>
              {menuItems.map((item, index) => {
                if (item.type === 'section') {
                  const sectionId = item.name.toLowerCase().replace(/\s+/g, '-');
                  const isOpen = openSections.includes(sectionId);
                  const isHovered = hoveredPath === sectionId;
                  return (
                    <li key={`section-${index}`} style={{ marginTop: index > 0 ? '4px' : '0' }}>
                      {collapsed ? (
                        <Tooltip title={item.name} placement="right">
                          <div 
                            onClick={() => toggleSection(sectionId)}
                            onMouseEnter={() => setHoveredPath(sectionId)}
                            onMouseLeave={() => setHoveredPath(null)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '14px',
                              padding: '12px',
                              margin: '0 8px',
                              borderRadius: '12px',
                              color: inactiveColor,
                              backgroundColor: isHovered ? hoverBg : 'transparent',
                              fontSize: '16px',
                              fontWeight: 400,
                              fontFamily: "'Rubik', sans-serif",
                              transition: 'all 0.2s ease-in-out',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            {item.icon(inactiveColor)}
                          </div>
                        </Tooltip>
                      ) : (
                        <div 
                          onClick={() => toggleSection(sectionId)}
                          onMouseEnter={() => setHoveredPath(sectionId)}
                          onMouseLeave={() => setHoveredPath(null)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '14px',
                            padding: '12px 16px',
                            margin: '0 12px',
                            borderRadius: '12px',
                            color: inactiveColor,
                            backgroundColor: isHovered ? hoverBg : 'transparent',
                            fontSize: '16px',
                            fontWeight: 400,
                            fontFamily: "'Rubik', sans-serif",
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '14px', whiteSpace: 'nowrap' }}>
                            {item.icon(inactiveColor)}
                            {item.name}
                          </span>
                          <span>
                            {isOpen ? <DownOutlined style={{ fontSize: '12px' }} /> : <RightOutlined style={{ fontSize: '12px' }} />}
                          </span>
                        </div>
                      )}
                      {isOpen && !collapsed && (
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0, 
                          margin: 0, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '4px' 
                        }}>
                          {item.items.map((subItem) => (
                            <li key={subItem.path}>
                              <MenuItem
                                to={subItem.path}
                                path={subItem.path}
                                label={subItem.name}
                                icon={subItem.icon}
                                isSubmenu={true}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }
                return (
                  <li key={item.path}>
                    <MenuItem
                      to={item.path}
                      path={item.path}
                      label={item.name}
                      icon={item.icon}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </Sider>

        {/* Scrollable Content Area */}
        <Layout
          style={{
            marginLeft: collapsed ? 80 : siderWidth,
            transition: 'margin-left 0.2s',
            minHeight: `calc(100vh - 80px)`,
            background: '#f0f2f5'
          }}
        >
          <Content style={{ 
            padding: 0, 
            overflow: 'auto',
            height: `calc(100vh - 80px)`
          }}>
            <div style={{ padding: '24px' }}>
              {/* Breadcrumb */}
              <div style={{ marginBottom: '24px' }}>
                <nav style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  fontSize: '18px',
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  <Link 
                    to="/admin" 
                    style={{ 
                      color: '#8c8c8c', 
                      textDecoration: 'none',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.color = primaryColor}
                    onMouseLeave={(e) => e.target.style.color = '#8c8c8c'}
                  >
                    Admin
                  </Link>
                  <span style={{ color: '#d9d9d9' }}>/</span>
                  <span style={{ color: '#262626', fontWeight: 500 }}>
                    {menuItems.find(item =>
                      location.pathname === item.path ||
                      (item.path !== '/admin' && location.pathname.startsWith(item.path))
                    )?.name || 'Dashboard'}
                  </span>
                </nav>
              </div>

              {/* Content Area */}
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px rgba(0,0,0,0.02)',
                padding: '24px',
                minHeight: 'calc(100vh - 200px)'
              }}>
                <Outlet />
              </div>
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AdminPanel;